import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Container,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Backdrop
} from '@mui/material';
import { ArrowBack, Search, Add, CheckCircle, Cancel, Title, Numbers, List as ListIcon, Delete, Edit, RestartAlt } from '@mui/icons-material';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncAttributes } from '../services/productService';

const attributeTypes = [
  { value: 'testo', label: 'Testo', icon: <Title fontSize="small" sx={{ mr: 1 }} /> },
  { value: 'numero', label: 'Numero', icon: <Numbers fontSize="small" sx={{ mr: 1 }} /> },
  { value: 'select', label: 'Select', icon: <ListIcon fontSize="small" sx={{ mr: 1 }} /> },
];

function TechyShopAttributi() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Selected attribute for details/edit
  const [selectedAttribute, setSelectedAttribute] = useState(null);

  // Edit attribute state
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('testo');
  const [editMandatory, setEditMandatory] = useState(false);

  // New attribute state
  const [newAttributeName, setNewAttributeName] = useState('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [attributeType, setAttributeType] = useState('testo');

  // New value state
  const [newValue, setNewValue] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni attributi e valori in tempo reale dal DB
  const attributi = useLiveQuery(() => db.techyshop_attributi.toArray(), []);
  const valori = useLiveQuery(() => db.techyshop_attributo_valori.toArray(), []);

  // Memorizza gli attributi filtrati
  const filteredAttributi = useMemo(() => {
    if (!attributi) return [];

    const lowercasedQuery = searchQuery.toLowerCase();

    return attributi
      .filter(attr => {
        const nome = attr.nome || '';
        const id = attr.id ? attr.id.toString() : '';
        const tipo = attr.tipo || '';

        return id.includes(lowercasedQuery) ||
          nome.toLowerCase().includes(lowercasedQuery) ||
          tipo.toLowerCase().includes(lowercasedQuery);
      });
  }, [searchQuery, attributi]);

  // Valori per l'attributo selezionato
  const selectedAttributeValues = useMemo(() => {
    if (!selectedAttribute || !valori) return [];
    return valori.filter(v => v.attributo_id === selectedAttribute.id);
  }, [selectedAttribute, valori]);

  const handleOpenAddModal = () => setIsAddModalOpen(true);
  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewAttributeName('');
    setIsMandatory(false);
    setAttributeType('testo');
  };

  const handleRowClick = (attr) => {
    setSelectedAttribute(attr);
    setEditName(attr.nome || '');
    setEditType(attr.tipo || 'testo');
    setEditMandatory(attr.obbligatorio === 1 || attr.obbligatorio === true);
    setIsEditMode(false);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedAttribute(null);
    setNewValue('');
    setIsEditMode(false);
  };

  const getTypeLabel = (type) => {
    const found = attributeTypes.find(t => t.value === type);
    return found ? found.label : type || 'Testo';
  };

  const handleSyncAttributi = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare tutti gli attributi locali e riscaricarli dal server?')) {
      return;
    }

    setIsSyncing(true);
    setToast({ open: true, message: 'Reset attributi in corso...', severity: 'info' });

    try {
      const result = await syncAttributes();
      setToast({
        open: true,
        message: result.message,
        severity: result.success ? 'success' : 'error'
      });
    } catch (error) {
      log("Errore nel reset attributi:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSaveAttribute = async () => {
    if (!newAttributeName.trim()) return;

    setIsSaving(true);
    const now = new Date();
    const formattedDate = now.toLocaleDateString('it-IT') + ' ' + now.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/addAttributo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({
          nome: newAttributeName,
          obbligatorio: isMandatory ? 1 : 0,
          tipo: attributeType
        }),
      });

      const result = await response.json();

      if (result.result) {
        const newId = result.details.id || result.details;
        await db.techyshop_attributi.add({
          id: newId,
          nome: newAttributeName,
          obbligatorio: isMandatory ? 1 : 0,
          tipo: attributeType,
          date: formattedDate
        });
        setToast({ open: true, message: 'Attributo creato con successo!', severity: 'success' });
        handleCloseAddModal();
      } else {
        throw new Error(result.text || "Errore durante la creazione dell'attributo");
      }
    } catch (error) {
      log("Errore nella creazione dell'attributo:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateAttribute = async () => {
    if (!selectedAttribute || !editName.trim()) return;

    setIsSaving(true);

    try {
      // Prepariamo i valori da modificare: solo quelli che sono cambiati
      const values = [];

      if (editName !== selectedAttribute.nome) {
        values.push({ field: 'nome', value: editName });
      }

      if (editType !== selectedAttribute.tipo) {
        values.push({ field: 'tipo', value: editType });
      }

      const editMandatoryValue = editMandatory ? 1 : 0;
      const currentMandatoryValue = (selectedAttribute.obbligatorio === 1 || selectedAttribute.obbligatorio === true) ? 1 : 0;

      if (editMandatoryValue !== currentMandatoryValue) {
        values.push({ field: 'obbligatorio', value: editMandatoryValue });
      }

      // Se non è cambiato nulla, non facciamo la chiamata
      if (values.length === 0) {
        setIsEditMode(false);
        setIsSaving(false);
        return;
      }

      const filters = [
        { field: 'id', value: selectedAttribute.id }
      ];

      const response = await fetch('https://deadapi.techysumo.it/techyshop/editAttributo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ values, filters }),
      });

      const result = await response.json();

      if (result.result) {
        // Aggiorna Dexie
        await db.techyshop_attributi.update(selectedAttribute.id, {
          nome: editName,
          tipo: editType,
          obbligatorio: editMandatory ? 1 : 0
        });

        // Aggiorna lo stato locale per riflettere i cambiamenti nel modal se ancora aperto
        setSelectedAttribute(prev => ({
          ...prev,
          nome: editName,
          tipo: editType,
          obbligatorio: editMandatory ? 1 : 0
        }));

        setIsEditMode(false);
        setToast({ open: true, message: 'Attributo aggiornato con successo!', severity: 'success' });
      } else {
        throw new Error(result.text || "Errore durante l'aggiornamento dell'attributo");
      }
    } catch (error) {
      log("Errore nell'aggiornamento dell'attributo:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddValue = async () => {
    if (!newValue.trim() || !selectedAttribute) return;

    setIsSaving(true);
    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/addAttributoValore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({
          attributo_id: selectedAttribute.id,
          valore: newValue
        }),
      });

      const result = await response.json();
      if (result.result) {
        const newValId = result.details.id || result.details;
        await db.techyshop_attributo_valori.add({
          id: newValId,
          attributo_id: selectedAttribute.id,
          valore: newValue
        });
        setNewValue('');
        setToast({ open: true, message: 'Valore aggiunto con successo!', severity: 'success' });
      } else {
        throw new Error(result.text || "Errore durante l'aggiunta del valore");
      }
    } catch (error) {
      log("Errore nell'aggiunta del valore:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteValue = async (valId) => {
    if (!selectedAttribute) return;

    const valObj = valori.find(v => v.id === valId);
    if (!valObj) return;

    if (!window.confirm(`Sei sicuro di voler eliminare questo valore? Verranno rimosse anche tutte le associazioni sui prodotti con il valore "${valObj.valore}".`)) return;

    setIsSaving(true);
    try {
      // 1. Elimina valore da Dexie
      await db.techyshop_attributo_valori.delete(valId);

      // 2. Elimina a cascata le associazioni sui prodotti in Dexie
      const associationsToDelete = await db.techyshop_prodotto_attributo
        .where('attributo_id').equals(selectedAttribute.id)
        .and(item => item.attributo_valore_id === valId)
        .toArray();

      if (associationsToDelete.length > 0) {
        const assocIds = associationsToDelete.map(a => a.id);
        await db.techyshop_prodotto_attributo.bulkDelete(assocIds);
        log(`Eliminate a cascata ${assocIds.length} associazioni per il valore "${valObj.valore}"`, 'INFO');
      }

      // 3. Chiamata API per eliminare sul server
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delAttributoValore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({
          id: valId
        }),
      });

      const result = await response.json();
      if (result.result) {
        setToast({ open: true, message: 'Valore eliminato con successo!', severity: 'success' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione del valore sul server");
      }
    } catch (error) {
      log("Errore nell'eliminazione del valore:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAttribute = async (attrId) => {
    const attrObj = attributi.find(a => a.id === attrId);
    if (!attrObj) return;

    if (!window.confirm(`Sei sicuro di voler eliminare l'attributo "${attrObj.nome}"? Verranno eliminati TUTTI i suoi valori e TUTTE le associazioni sui prodotti.`)) return;

    setIsSaving(true);
    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delAttributo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ id: attrId }),
      });

      const result = await response.json();
      if (result.result) {
        // 1. Elimina l'attributo da Dexie
        await db.techyshop_attributi.delete(attrId);

        // 2. Elimina i valori associati da Dexie
        const valuesToDelete = await db.techyshop_attributo_valori.where('attributo_id').equals(attrId).toArray();
        const valueIds = valuesToDelete.map(v => v.id);
        if (valueIds.length > 0) {
          await db.techyshop_attributo_valori.bulkDelete(valueIds);
        }

        // 3. Elimina a cascata tutte le associazioni prodotto per questo attributo
        const associationsToDelete = await db.techyshop_prodotto_attributo
          .where('attributo_id').equals(attrId)
          .toArray();

        if (associationsToDelete.length > 0) {
          const assocIds = associationsToDelete.map(a => a.id);
          await db.techyshop_prodotto_attributo.bulkDelete(assocIds);
          log(`Eliminate a cascata ${assocIds.length} associazioni per l'attributo "${attrObj.nome}"`, 'INFO');
        }

        setToast({ open: true, message: 'Attributo eliminato con successo!', severity: 'success' });
        if (selectedAttribute && selectedAttribute.id === attrId) {
          handleCloseDetailModal();
        }
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione dell'attributo");
      }
    } catch (error) {
      log("Errore nell'eliminazione dell'attributo:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  if (!attributi) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Caricamento attributi...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}
        open={isSyncing}
      >
        <Loader />
        <Typography variant="h6">Sincronizzazione attributi in corso...</Typography>
      </Backdrop>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/techyshop')} aria-label="back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ ml: 2, flexGrow: 1 }}>
          Attributi Prodotti
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          color="warning"
          startIcon={isSyncing ? <CircularProgress size={20} color="inherit" /> : <RestartAlt />}
          onClick={handleSyncAttributi}
          disabled={isSyncing}
        >
          Reset Attributi
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleOpenAddModal}
        >
          Aggiungi Nuovo Attributo
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Cerca per nome, ID o tipo"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nome Attributo</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Tipo</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Obbligatorio</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Data Aggiunta</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAttributi.map((attr) => (
              <TableRow
                key={attr.id}
                hover
                onClick={() => handleRowClick(attr)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
                }}
              >
                <TableCell sx={{ color: 'white' }}>{attr.id}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{attr.nome}</TableCell>
                <TableCell>
                  <Chip
                    label={getTypeLabel(attr.tipo)}
                    size="small"
                    variant="outlined"
                    sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)' }}
                  />
                </TableCell>
                <TableCell align="center">
                  {(attr.obbligatorio === 1 || attr.obbligatorio === true) ? (
                    <Chip
                      icon={<CheckCircle style={{ color: '#4dab56' }} />}
                      label="Sì"
                      size="small"
                      sx={{ backgroundColor: 'rgba(77, 171, 86, 0.1)', color: '#4dab56', border: '1px solid rgba(77, 171, 86, 0.3)' }}
                    />
                  ) : (
                    <Chip
                      icon={<Cancel style={{ color: '#f44336' }} />}
                      label="No"
                      size="small"
                      sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#f44336', border: '1px solid rgba(244, 67, 54, 0.3)' }}
                    />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  {attr.date || attr.data_aggiunta || attr.date_ins || attr.created_at || '--/--/----'}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => { e.stopPropagation(); handleRowClick(attr); setIsEditMode(true); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => { e.stopPropagation(); handleDeleteAttribute(attr.id); }}
                    disabled={isSaving}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredAttributi.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Nessun attributo trovato.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Aggiungi Attributo */}
      <Dialog open={isAddModalOpen} onClose={handleCloseAddModal} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Aggiungi Nuovo Attributo</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label="Nome Attributo"
              value={newAttributeName}
              onChange={(e) => setNewAttributeName(e.target.value)}
              disabled={isSaving}
            />

            <FormControl fullWidth disabled={isSaving}>
              <InputLabel id="type-label">Tipo Dato</InputLabel>
              <Select
                labelId="type-label"
                value={attributeType}
                label="Tipo Dato"
                onChange={(e) => setAttributeType(e.target.value)}
              >
                {attributeTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isMandatory}
                  onChange={(e) => setIsMandatory(e.target.checked)}
                  color="primary"
                />
              }
              label="Attributo Obbligatorio"
              disabled={isSaving}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddModal} disabled={isSaving}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSaveAttribute}
            disabled={isSaving || !newAttributeName.trim()}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Dettaglio/Modifica Attributo */}
      <Dialog open={isDetailModalOpen} onClose={handleCloseDetailModal} fullScreen>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" color="inherit" onClick={handleCloseDetailModal} aria-label="close" sx={{ mr: 2 }}>
              <Cancel />
            </IconButton>
            <Typography variant="h6" component="span">
              {isEditMode ? `Modifica Attributo: ${selectedAttribute?.nome}` : `Dettaglio Attributo: ${selectedAttribute?.nome}`}
            </Typography>
          </Box>
          <Chip label={`ID: ${selectedAttribute?.id}`} size="small" />
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" color="primary">Informazioni Base</Typography>
                {!isEditMode && (
                  <IconButton size="small" color="primary" onClick={() => setIsEditMode(true)}>
                    <Edit fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {isEditMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome Attributo"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    disabled={isSaving}
                  />

                  <FormControl fullWidth size="small" disabled={isSaving}>
                    <InputLabel id="edit-type-label">Tipo Dato</InputLabel>
                    <Select
                      labelId="edit-type-label"
                      value={editType}
                      label="Tipo Dato"
                      onChange={(e) => setEditType(e.target.value)}
                    >
                      {attributeTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {type.icon}
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editMandatory}
                        onChange={(e) => setEditMandatory(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Obbligatorio"
                    disabled={isSaving}
                  />

                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="small"
                      onClick={handleUpdateAttribute}
                      disabled={isSaving || !editName.trim()}
                    >
                      {isSaving ? <CircularProgress size={20} /> : 'Salva Modifiche'}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={() => setIsEditMode(false)}
                      disabled={isSaving}
                    >
                      Annulla
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <Typography variant="body1"><strong>Nome:</strong> {selectedAttribute?.nome}</Typography>
                  <Typography variant="body1"><strong>Tipo:</strong> {getTypeLabel(selectedAttribute?.tipo)}</Typography>
                  <Typography variant="body1">
                    <strong>Obbligatorio:</strong> {(selectedAttribute?.obbligatorio === 1 || selectedAttribute?.obbligatorio === true) ? 'Sì' : 'No'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    Aggiunto il: {selectedAttribute?.date || selectedAttribute?.data_aggiunta || 'N/A'}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Valori Possibili</Typography>
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Nuovo valore"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddValue()}
                    disabled={isSaving}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddValue}
                    disabled={isSaving || !newValue.trim()}
                  >
                    <Add />
                  </Button>
                </Box>

                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <List dense>
                    {selectedAttributeValues.map((val) => (
                      <ListItem key={val.id} divider>
                        <ListItemText primary={val.valore} />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteValue(val.id)}
                            disabled={isSaving}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                    {selectedAttributeValues.length === 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Nessun valore definito"
                          primaryTypographyProps={{ style: { fontStyle: 'italic', color: 'rgba(255,255,255,0.4)', textAlign: 'center' } }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => handleDeleteAttribute(selectedAttribute?.id)}
            disabled={isSaving}
          >
            Elimina Attributo
          </Button>
          <Button onClick={handleCloseDetailModal} color="primary">Chiudi</Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopAttributi;
